<template>
  <section class="detail-card">
    <h2>{{ reportTitle }}</h2>

    <div class="metadata-grid">
      <div class="metadata-item">
        <span>{{ $t("reports.dateOfReport") }}</span>
        <strong>{{ reportDate }}</strong>
      </div>
      <div class="metadata-item">
        <span>{{ $t("reports.farm") }}</span>
        <strong>{{ farmName }}</strong>
      </div>
      <div class="metadata-item">
        <span>{{ $t("reports.zone") }}</span>
        <strong>{{ zoneName }}</strong>
      </div>
      <div class="metadata-item">
        <span>{{ $t("reports.location") }}</span>
        <strong>{{ location }}</strong>
        <small>{{ coordinates }}</small>
      </div>
      <div class="metadata-item">
        <span>{{ $t("reports.crop") }}</span>
        <strong>{{ cropName }}</strong>
      </div>
    </div>

    <div class="map-frame">
      <slot name="map" />
      <button
        class="map-arrow left"
        type="button"
        :disabled="imageLayers.length <= 1"
        aria-label="Previous image"
        @click="previousImage"
      >
        <v-icon size="34">mdi-chevron-left</v-icon>
      </button>
      <button
        class="map-arrow right"
        type="button"
        :disabled="imageLayers.length <= 1"
        aria-label="Next image"
        @click="nextImage"
      >
        <v-icon size="34">mdi-chevron-right</v-icon>
      </button>
      <div v-if="activeImageLayer" class="active-layer-label">
        {{ activeImageLayer.label }}
      </div>
    </div>

    <p class="imagery-note">
      {{ $t("reports.satelliteImageryNote") }}
    </p>

    <div v-if="imageLayers.length" class="date-strip">
      <strong>Map layer</strong>
      <button
        v-for="(layer, index) in imageLayers"
        :key="layer.key || index"
        type="button"
        class="layer-button"
        :class="{ selected: index === activeImageIndex }"
        @click="selectImage(index)"
      >
        {{ layer.label }}
      </button>
    </div>

    <div class="report-details">
      <h3>{{ $t("reports.landSuitabilityDesign.reportDetails") }}</h3>
      <anomaly-bands
        :bands="distribution"
        row
        i18n-prefix="reports.landSuitabilityDesign"
      />
      <anomaly-colorbar
        class="mt-4"
        i18n-prefix="reports.landSuitabilityDesign"
      />
    </div>

    <div class="disclaimer">
      <v-icon size="18" color="#d4a829">mdi-alert-outline</v-icon>
      <div>
        <strong>{{ $t("reports.disclaimer") }}</strong>
        <p>
          {{ $t("reports.landSuitabilityDisclaimer") }}
        </p>
      </div>
    </div>

    <v-divider class="my-4" />

    <feedback-display
      :feedback="feedback"
      i18n-prefix="reports.landSuitabilityDesign"
      @add="showFeedback = true"
    />

    <feedback-dialog
      :value="showFeedback"
      :report-uid="feedbackReportUid"
      :service="feedbackService"
      i18n-prefix="reports.landSuitabilityDesign"
      upload-folder="land-suitability/feedback"
      @input="showFeedback = $event"
      @submitted="onFeedbackSubmitted"
    />
  </section>
</template>

<script>
import moment from "moment";
import LandSuitabilityAndWeatherService from "@/_services/LandSuitabilityAndWeatherService";
import FeedbackDialog from "../../shared/FeedbackDialog.vue";
import FeedbackDisplay from "../../shared/FeedbackDisplay.vue";
import AnomalyBands from "../../anomaly-detection/components/AnomalyBands.vue";
import AnomalyColorbar from "../../anomaly-detection/components/AnomalyColorbar.vue";

export default {
  name: "LandSuitabilityDetailReport",
  components: { FeedbackDialog, FeedbackDisplay, AnomalyBands, AnomalyColorbar },
  props: {
    details: {
      type: Object,
      default: null,
    },
    farm: {
      type: Object,
      default: null,
    },
    reportId: {
      type: [String, Number],
      required: true,
    },
    imageLayers: {
      type: Array,
      default: () => [],
    },
    activeImageIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      feedback: null,
      showFeedback: false,
    };
  },
  computed: {
    feedbackService() {
      return LandSuitabilityAndWeatherService;
    },
    activeImageLayer() {
      return this.imageLayers[this.activeImageIndex] || this.imageLayers[0] || null;
    },
    feedbackReportUid() {
      return String(this.reportId);
    },
    reportTitle() {
      return `${this.farmName} Report`;
    },
    farmName() {
      return (this.farm && this.farm.farmName) ||
        (this.details && this.details.userFarm && this.details.userFarm.farmName) ||
        this.$t("reports.landSuitability");
    },
    zoneName() {
      return (this.details && (this.details.zoneName || this.details.zone_name)) || "-";
    },
    cropName() {
      return (this.details && (this.details.crop_name || this.details.cropName)) || "—";
    },
    reportDate() {
      const date = this.details && (this.details.issuedDate || this.details.createdAt);
      return date ? moment(date).format("DD-MMM-YYYY").toUpperCase() : "—";
    },
    startDate() {
      const date = this.details &&
        (this.details.startDate || this.details.start_date || this.details.createdAt);
      return date ? moment(date).format("DD-MMM-YYYY").toUpperCase() : "—";
    },
    location() {
      if (!this.details) return "—";
      return [
        this.details.address,
        this.details.state,
        this.details.country,
      ].filter(Boolean).join(", ") || this.$t("reports.farmLocation");
    },
    coordinates() {
      if (!this.details) return "";
      const latitude = Number(this.details.latitude);
      const longitude = Number(this.details.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return "";
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    },
    historicalCount() {
      return (this.details && (
        this.details.historicalReportsCount ||
        this.details.historical_reports_count
      )) || 1;
    },
    distribution() {
      const rows = this.details && Array.isArray(this.details.land_eval_details)
        ? this.details.land_eval_details
        : [];
      if (!rows.length) {
        const score = Math.max(
          0,
          Math.min(100, Math.round(Number(this.details && this.details.overall_score) || 0))
        );
        return { green: score, yellow: 100 - score, red: 0 };
      }

      const counts = rows.reduce((result, row) => {
        const status = String(row.originalStatus || row.status || "").toLowerCase();
        if (status.includes("highly")) result.green += 1;
        else if (status.includes("unsuitable")) result.red += 1;
        else result.yellow += 1;
        return result;
      }, { green: 0, yellow: 0, red: 0 });

      const total = rows.length;
      const green = Math.round((counts.green / total) * 100);
      const red = Math.round((counts.red / total) * 100);
      return { green, yellow: 100 - green - red, red };
    },
    imageDates() {
      const base = this.details &&
        (this.details.issuedDate || this.details.createdAt)
        ? moment(this.details.issuedDate || this.details.createdAt)
        : moment();
      return [-120, -90, -60, -30, 0, 30, 60, 90, 120].map((days) => ({
        label: base.clone().add(days, "days").format("DD-MMM-YYYY").toUpperCase(),
        selected: days === 0,
      }));
    },
  },
  mounted() {
    this.loadFeedback();
  },
  methods: {
    previousImage() {
      this.$emit("previous-image");
    },
    nextImage() {
      this.$emit("next-image");
    },
    selectImage(index) {
      this.$emit("select-image", index);
    },
    showCompareUnavailable() {
      this.$notify({
        title: this.$t("reports.historicalComparisonUnavailable"),
        type: "warning",
      });
    },
    async loadFeedback() {
      if (!this.reportId) return;
      try {
        const response = await LandSuitabilityAndWeatherService.getLandSuitabilityFeedback(
          this.reportId
        );
        if (response && response.success === false) {
          throw new Error(response.message || response.msg || this.$t("reports.unableToLoadFeedback"));
        }
        this.feedback = response && response.data ? response.data.feedback : null;
      } catch (error) {
        this.$notify({
          title: this.$t("reports.unableToLoadReportFeedback"),
          type: "error",
        });
      }
    },
    onFeedbackSubmitted() {
      this.showFeedback = false;
      this.$notify({
        title: this.$t("success"),
        text: this.$t("reports.landSuitabilityDesign.feedbackSubmitted"),
        type: "success",
      });
      this.loadFeedback();
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-card {
  padding: 20px 24px 34px;
  background: #fff;

  > h2 {
    margin: 0 0 24px;
    font-size: 18px;
  }
}

.metadata-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1.3fr;
  gap: 24px 40px;
  margin-bottom: 18px;
}

.metadata-item {
  display: flex;
  flex-direction: column;
  min-width: 0;

  span {
    margin-bottom: 8px;
    color: #67717a;
    font-size: 11px;
  }

  strong {
    font-size: 12px;
    font-weight: 500;
  }

  small {
    margin-top: 3px;
    color: #535d65;
    font-size: 10px;
  }
}

.compare-button {
  height: 38px !important;
  margin: 2px 0 18px;
  border-radius: 3px;
  box-shadow: none;
  font-size: 11px;
  font-weight: 700;
}

.map-frame {
  position: relative;
  overflow: hidden;
}

.map-arrow {
  position: absolute;
  top: 50%;
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-50%);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  &.left {
    left: 18px;
  }

  &.right {
    right: 18px;
  }
}

.active-layer-label {
  position: absolute;
  bottom: 14px;
  left: 50%;
  padding: 6px 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  color: #263238;
  font-size: 12px;
  font-weight: 600;
  transform: translateX(-50%);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

.imagery-note {
  margin: 12px 0 14px;
  color: #313a41;
  font-size: 10px;
  font-style: italic;
}

.date-strip {
  display: flex;
  min-height: 54px;
  align-items: stretch;
  overflow-x: auto;
  padding: 0 16px;
  border: 1px solid #dde2e5;
  border-radius: 3px;
  box-shadow: 0 2px 7px rgba(29, 45, 37, 0.1);

  strong {
    display: flex;
    min-width: 92px;
    align-items: center;
    color: #434b51;
    font-size: 12px;
  }

  .layer-button {
    min-width: max-content;
    padding: 16px 12px 13px;
    color: #8a9298;
    font-size: 11px;
    white-space: nowrap;
  }

  .layer-button.selected {
    border-bottom: 3px solid #20ae75;
    color: #169462;
    font-weight: 700;
  }
}

.report-details {
  margin-top: 18px;
  padding: 22px 0 28px;
  border-top: 1px solid #e0e4e6;
  border-bottom: 1px solid #e0e4e6;

  h3 {
    margin: 0 0 18px;
    color: #18a36c;
    font-size: 14px;
    font-weight: 700;
  }
}


.disclaimer {
  display: flex;
  gap: 10px;
  margin: 20px 0 18px;
  padding: 12px 14px;
  border: 1px solid #e8dba5;
  border-radius: 3px;
  background: #fffcf0;

  strong {
    color: #90751d;
    font-size: 11px;
  }

  p {
    margin: 4px 0 0;
    color: #6b6656;
    font-size: 10px;
    line-height: 1.4;
  }
}


@media (max-width: 1000px) {
  .metadata-grid {
    grid-template-columns: 1fr 1fr;
  }

  .date-strip {
    display: flex;
    gap: 18px;
    overflow-x: auto;
  }
}
</style>
